// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for creating new asset lifecycle events.
 * Provides functionality to record significant points in an asset's history,
 * such as maintenance, inspections, or status changes.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { eventSchema } from '@/asset/api/types';
import { DateTime } from 'luxon';

/**
 * Schema for validating the API response when creating an event.
 * Transforms the response to provide a more intuitive structure with
 * the event data separated from metadata.
 */
const createEventResponseSchema = z
  .object({
    data: eventSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, event: data }));

type CreateEventResponse = z.infer<typeof createEventResponseSchema>;

/**
 * Parameters required for creating a new asset event.
 * @typedef {Object} CreateEventParameters
 * @property {string} typeId - Unique identifier for the type of event to create
 * @property {DateTime} occurredAt - Date and time when the event occurred
 */
export interface CreateEventParameters {
  typeId: string;
  occurredAt: DateTime;
}

/**
 * Creates a new event in an asset's lifecycle.
 * This function handles the API call to record significant points in time
 * for an asset and validates the response data using Zod schemas.
 * Events can represent various activities like maintenance, inspections,
 * or status changes.
 *
 * @param assetId - Unique identifier of the asset to create the event for
 * @param parameters - Object containing the event details
 * @returns Promise resolving to the created event data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function createEvent(
  assetId: string,
  parameters: CreateEventParameters
): Promise<CreateEventResponse> {
  const url = `/v1/asset/${assetId}/event`;
  const response = await api.post(url, {
    ...parameters,
    occurredAt: parameters.occurredAt.toUTC().toISO(),
  });
  const result = await parseResponse(createEventResponseSchema, response.data, {
    schemaName: 'CreateEvent',
    endpoint: url,
  });

  return result;
}
